// contexts/UnreadCountContext.tsx
'use client';
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useCookieConsent } from '@/hooks/animation/useCookieConsent';

interface UnreadCountContextType {
  count: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const UnreadCountContext = createContext<UnreadCountContextType>({
  count: 0,
  loading: true,
  error: null,
  refresh: () => {},
});

export const useUnreadCountContext = () => useContext(UnreadCountContext);

export const UnreadCountProvider: React.FC<{ children: React.ReactNode; interval?: number }> = ({ children, interval = 30000 }) => {
  const { data: session, status } = useSession();
  const { needsConsent } = useCookieConsent();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const eventListenerAdded = useRef(false);

  const fetchUnreadCount = useCallback(async () => {
    if (needsConsent || !session || status !== 'authenticated') {
      setCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setError(null);
      console.log(`[UnreadCount] Appel API à ${new Date().toISOString()}`);
      const response = await fetch('/api/messages/unread-count');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setCount(data.count || 0);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [session, status, needsConsent]);

  // Rafraîchissement unique
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Intervalle unique
  useEffect(() => {
    if (interval > 0 && !needsConsent && status === 'authenticated') {
      intervalRef.current = setInterval(fetchUnreadCount, interval);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchUnreadCount, interval, needsConsent, status]);

  // Écouter l’événement "message marqué comme lu" une seule fois
  useEffect(() => {
    if (eventListenerAdded.current) return;
    eventListenerAdded.current = true;

    const handleMessageRead = () => {
      if (!needsConsent && status === 'authenticated') {
        fetchUnreadCount();
      }
    };

    window.addEventListener('messageMarkedAsRead', handleMessageRead);
    return () => {
      window.removeEventListener('messageMarkedAsRead', handleMessageRead);
      eventListenerAdded.current = false;
    };
  }, [fetchUnreadCount, needsConsent, status]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return (
    <UnreadCountContext.Provider value={{ count, loading, error, refresh }}>
      {children}
    </UnreadCountContext.Provider>
  );
};